/**
 * Pure URL builders for the Mouse staff API, `/:orgId/ordering/*`.
 *
 * Kept apart from the RTK slice so the paths can be unit-tested without a
 * store, and so a contract change is one edit here rather than a hunt through
 * forty endpoints. Query strings are built here too: `undefined` and empty
 * values are dropped so `?venue=` never reaches the wire.
 */

type QueryValue = string | number | boolean | undefined | null

export function query(params: Record<string, QueryValue>): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue
    parts.push(
      `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
  }
  return parts.length ? `?${parts.join('&')}` : ''
}

const enc = encodeURIComponent

export function orderingRoot(orgId: string): string {
  return `/${enc(orgId)}/ordering`
}

export const mouseUrls = {
  capabilities: (orgId: string) => `${orderingRoot(orgId)}/capabilities`,

  // Menus
  menus: (orgId: string, venue?: string) =>
    `${orderingRoot(orgId)}/menus${query({ venue })}`,
  generateMenu: (orgId: string) => `${orderingRoot(orgId)}/menus/generate`,

  // Menu import: a PDF or page images read by the model, reviewed, committed.
  menuImports: (orgId: string, venue?: string, limit?: number) =>
    `${orderingRoot(orgId)}/menu-imports${query({ venue, limit })}`,
  menuImport: (orgId: string, importId: string) =>
    `${orderingRoot(orgId)}/menu-imports/${enc(importId)}`,
  menuImportCommit: (orgId: string, importId: string) =>
    `${orderingRoot(orgId)}/menu-imports/${enc(importId)}/commit`,
  generateMenuApply: (orgId: string) =>
    `${orderingRoot(orgId)}/menus/generate/apply`,
  menu: (orgId: string, menuId: string, venue?: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}${query({ venue })}`,
  /**
   * A menu's trade over a window, as an article.
   *
   * The day and the times go as LOCAL strings, not instants: only Mouse knows
   * the venue's timezone, so sending an ISO instant from the browser would
   * ask for a different day's trade than the one on screen.
   */
  menuReport: (
    orgId: string,
    menuId: string,
    params: { date?: string; from?: string; to?: string } = {}
  ) => `${orderingRoot(orgId)}/menus/${enc(menuId)}/report${query(params)}`,
  /**
   * A menu's day as a replay.
   *
   * `serial` as well as the day, because a floor plan is one per venue and a
   * menu is not: Mouse refuses rather than picking one, so the caller has to
   * say which room to play.
   */
  menuReplay: (
    orgId: string,
    menuId: string,
    params: { serial?: string; date?: string; from?: string; to?: string } = {}
  ) => `${orderingRoot(orgId)}/menus/${enc(menuId)}/replay${query(params)}`,
  venueSpaceCatalogue: (orgId: string) =>
    `${orderingRoot(orgId)}/venue-space/catalogue`,
  venueSpace: (orgId: string, serial: string) =>
    `${orderingRoot(orgId)}/venue-space/${enc(serial)}`,
  venueSpaceCommands: (orgId: string, serial: string) =>
    `${orderingRoot(orgId)}/venue-space/${enc(serial)}/commands`,
  venueSpacePublish: (orgId: string, serial: string) =>
    `${orderingRoot(orgId)}/venue-space/${enc(serial)}/publish`,
  menuCrossSells: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/cross-sells`,
  menuVenues: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/venues`,
  /** A menu's zones AT ONE VENUE: an org menu carries one list per venue. */
  menuVenueZones: (orgId: string, menuId: string, serial: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/venues/${enc(serial)}/zones`,
  menuPolicy: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/policy`,
  menuDuplicate: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/duplicate`,
  menuHours: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/hours`,
  menuMedia: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/media`,
  sectionHours: (orgId: string, menuId: string, sectionId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/${enc(sectionId)}/hours`,
  sectionMedia: (orgId: string, menuId: string, sectionId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/${enc(sectionId)}/media`,
  productHours: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/hours`,
  sections: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections`,
  sectionsOrder: (orgId: string, menuId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/order`,
  section: (orgId: string, menuId: string, sectionId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/${enc(sectionId)}`,
  sectionProducts: (orgId: string, menuId: string, sectionId: string) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/${enc(sectionId)}/products`,
  sectionProduct: (
    orgId: string,
    menuId: string,
    sectionId: string,
    productId: string
  ) =>
    `${orderingRoot(orgId)}/menus/${enc(menuId)}/sections/${enc(sectionId)}/products/${enc(productId)}`,

  // Money: which account a menu's takings land in
  paymentAccounts: (orgId: string) => `${orderingRoot(orgId)}/payment-accounts`,
  stripePaymentAccount: (orgId: string, integrationId?: string) =>
    integrationId
      ? `${orderingRoot(orgId)}/payment-accounts/stripe/${enc(integrationId)}`
      : `${orderingRoot(orgId)}/payment-accounts/stripe`,

  // Catalogue
  catalogues: (orgId: string) => `${orderingRoot(orgId)}/catalogues`,
  stampedeCatalogue: (orgId: string) =>
    `${orderingRoot(orgId)}/catalogues/stampede`,
  /** Connects a venue's Square catalogue, asking Neo rather than zion. */
  squareCatalogue: (orgId: string) =>
    `${orderingRoot(orgId)}/catalogues/square`,
  /** POST here creates into the org's Stampede catalogue. */
  products: (orgId: string) => `${orderingRoot(orgId)}/products`,
  productModifierGroups: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/modifier-groups`,
  productReplaceWith: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/replace-with`,
  productVariants: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/variants`,
  variant: (orgId: string, variantId: string) =>
    `${orderingRoot(orgId)}/variants/${enc(variantId)}`,
  groupModifiers: (orgId: string, groupId: string) =>
    `${orderingRoot(orgId)}/modifier-groups/${enc(groupId)}/modifiers`,
  catalogueProducts: (
    orgId: string,
    catalogueId: string,
    params: {
      venue?: string
      search?: string
      cursor?: string
      limit?: number
      /** With a venue: only what that venue sells, per Square's own answer. */
      sold_here?: 'true'
      /** One Square catalogue object id: resolves the product it maps to. */
      square_ref?: string
    }
  ) =>
    `${orderingRoot(orgId)}/catalogues/${enc(catalogueId)}/products${query(params)}`,
  catalogueModifierGroups: (orgId: string, catalogueId: string) =>
    `${orderingRoot(orgId)}/catalogues/${enc(catalogueId)}/modifier-groups`,
  catalogueSync: (orgId: string, catalogueId: string) =>
    `${orderingRoot(orgId)}/catalogues/${enc(catalogueId)}/sync`,
  catalogueSyncJob: (orgId: string, catalogueId: string, jobId: string) =>
    `${orderingRoot(orgId)}/catalogues/${enc(catalogueId)}/sync/${enc(jobId)}`,
  product: (orgId: string, productId: string, venue?: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}${query({ venue })}`,
  productTags: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/tags`,
  productMedia: (orgId: string, productId: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/media`,
  productOverride: (orgId: string, productId: string, serial: string) =>
    `${orderingRoot(orgId)}/products/${enc(productId)}/venues/${enc(serial)}/override`,
  modifierGroup: (orgId: string, groupId: string) =>
    `${orderingRoot(orgId)}/modifier-groups/${enc(groupId)}`,
  modifier: (orgId: string, modifierId: string) =>
    `${orderingRoot(orgId)}/modifiers/${enc(modifierId)}`,
  modifierTags: (orgId: string, modifierId: string) =>
    `${orderingRoot(orgId)}/modifiers/${enc(modifierId)}/tags`,
  modifierGroupHidden: (orgId: string, groupId: string) =>
    `${orderingRoot(orgId)}/modifier-groups/${enc(groupId)}/hidden`,
  modifierHidden: (orgId: string, modifierId: string) =>
    `${orderingRoot(orgId)}/modifiers/${enc(modifierId)}/hidden`,
  tags: (orgId: string, kind?: string) =>
    `${orderingRoot(orgId)}/tags${query({ kind })}`,

  // Ordering points
  points: (orgId: string, venue?: string) =>
    `${orderingRoot(orgId)}/points${query({ venue })}`,
  point: (orgId: string, pointId: string) =>
    `${orderingRoot(orgId)}/points/${enc(pointId)}`,
  pointRotate: (orgId: string, pointId: string) =>
    `${orderingRoot(orgId)}/points/${enc(pointId)}/rotate-token`,
  pointsBulk: (orgId: string) => `${orderingRoot(orgId)}/points/bulk`,
  /** The zone names in use, per venue; derived from the points themselves. */
  zones: (orgId: string, venue?: string) =>
    `${orderingRoot(orgId)}/zones${query({ venue })}`,
  pointQr: (orgId: string, pointId: string) =>
    `${orderingRoot(orgId)}/points/${enc(pointId)}/qr`,
  /** Per-venue ordering settings: how it serves an order, and the picker's wording. */
  venueSettings: (orgId: string, serial: string) =>
    `${orderingRoot(orgId)}/venues/${enc(serial)}/settings`,

  // Orders
  orders: (
    orgId: string,
    params: {
      venue?: string
      /** Comma-separated statuses. */
      status?: string
      /** Comma-separated payment states. */
      payment_status?: string
      /** Comma-separated Mouse channels, e.g. `walk_in,pos` for "Till". */
      channel?: string
      /** Comma-separated contexts (`booking`, `enquiry`, `ticket_order`). */
      context_type?: string
      /** Which date `from`/`to` mean, and the page's order. Omitted is `placed`. */
      date_field?: 'placed' | 'service'
      from?: string
      to?: string
      /** One code: our reference, a Square payment id, or a receipt number. */
      q?: string
      cursor?: string
      limit?: number
    }
  ) => `${orderingRoot(orgId)}/orders${query(params)}`,
  /** The same filters as `orders`, folded. `group_by` is a CSV of dimensions. */
  ordersSummary: (
    orgId: string,
    params: Record<string, string | number | undefined>
  ) => `${orderingRoot(orgId)}/orders/summary${query(params)}`,
  order: (orgId: string, orderId: string) =>
    `${orderingRoot(orgId)}/orders/${enc(orderId)}`,
  orderRefunds: (orgId: string, orderId: string) =>
    `${orderingRoot(orgId)}/orders/${enc(orderId)}/refunds`,

  /**
   * Every text the ordering service sent, and what became of it.
   *
   * `from` and `to` are INSTANTS, not the local day strings the menu report
   * takes: the log spans every venue on the org and they are not all in one
   * timezone, so the window has to be unambiguous on the wire.
   */
  messages: (
    orgId: string,
    params: {
      from: string
      to: string
      serial?: string
      /** `delivered`, `failed`, or `pending` for not-yet-settled. */
      outcome?: string
      cursor?: string
      limit?: number
    }
  ) => `${orderingRoot(orgId)}/messages${query(params)}`,

  /**
   * What guests said about the ordering APP. Stampede only; the page that
   * calls this is gated on `internal.staff`.
   */
  appFeedback: (
    orgId: string,
    params: {
      from?: string
      to?: string
      serial?: string
      stars?: number
      with_comment?: boolean
      cursor?: string
      limit?: number
    }
  ) => `${orderingRoot(orgId)}/experience-reviews${query(params)}`,
} as const
